import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppHeader, Button, Dialog, Loader, Notice } from '@origo/origo-ui';
import { CheckIcon } from '@phosphor-icons/react';
import { ApiError } from '../../api/client';
import { usePreflight, useUpdateBinding } from '../../api/hooks';
import { type AgolLayerInfo, type SyncBinding } from '../../api/types';
import { toUpsertRequest, parseMappingProfile } from './bindingRequest';
import {
    type FieldDraft,
    type LayerDraft,
    applyProfileToDraft,
    buildMappingProfile,
    draftFromReport,
    layersUsedBy,
    mappingComplete,
    mappingProblem,
    reassignLayer,
    resolveLokalIdField,
} from './create/draft';
import { LayerList } from './create/LayerList';
import { LayerMappingPanel } from './create/LayerMappingPanel';
import styles from './create/create.module.css';

interface EditMappingFlowProps {
    open: boolean;
    /** Koblingen mappingen redigeres for (null når flyten er lukket). */
    binding: SyncBinding | null;
    onClose: () => void;
    onSaved: (binding: SyncBinding) => void;
}

function errorMessage(error: unknown, fallback: string): string | null {
    if (error instanceof ApiError) return error.message;
    return error ? fallback : null;
}

/**
 * Fullskjerm-takeover for å redigere felt-mappingen på en eksisterende kobling. Alle koblinger
 * har en mapping-profil, så flyten gjelder alle. Kjører preflight på nytt for å hente dagens
 * AGOL-lag/-felt og verdimap-forslag, og overlegger den lagrede profilen slik at brukerens valg
 * er forhåndsutfylt. Lagring er en PUT som bytter ut profilen.
 */
export const EditMappingFlow: React.FC<EditMappingFlowProps> = ({
    open,
    binding,
    onClose,
    onSaved,
}) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const preflight = usePreflight();
    const updateBinding = useUpdateBinding();

    const [layers, setLayers] = useState<LayerDraft[]>([]);
    const [availableAgolLayers, setAvailableAgolLayers] = useState<AgolLayerInfo[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [dirty, setDirty] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);

    // Synk dialog open/close mot <dialog>.
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (open) {
            if (!el.open) el.showModal();
        } else if (el.open) {
            el.close();
        }
    }, [open]);

    // Henter preflight på nytt og overlegger den lagrede mapping-profilen.
    const loadDraft = useCallback(async () => {
        if (!binding) return;
        setLayers([]);
        setSelectedIndex(null);
        try {
            const report = await preflight.mutateAsync({
                forvaltningssystem: binding.forvaltningssystem,
                centralDatasetId: binding.centralDatasetId,
                agolBaseUrl: binding.agolBaseUrl,
            });
            const agolLayers = report.availableAgolLayers ?? [];
            const overlaid = applyProfileToDraft(
                draftFromReport(report),
                parseMappingProfile(binding.mappingProfileJson),
                agolLayers,
            );
            setAvailableAgolLayers(agolLayers);
            setLayers(overlaid);
            setSelectedIndex(overlaid.length > 0 ? 0 : null);
        } catch {
            /* feil vises via preflight.error */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [binding?.bindingId]);

    // Last mappingen når flyten åpnes.
    useEffect(() => {
        if (!open || !binding) return;
        setAvailableAgolLayers([]);
        setDirty(false);
        setConfirmCancel(false);
        updateBinding.reset();
        void loadDraft();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, binding?.bindingId]);

    const requestCancel = () => {
        if (dirty) setConfirmCancel(true);
        else onClose();
    };

    // ESC: bekreft før lukking når noe er endret.
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        const handleCancel = (e: Event) => {
            e.preventDefault();
            requestCancel();
        };
        el.addEventListener('cancel', handleCancel);
        return () => el.removeEventListener('cancel', handleCancel);
    });

    const updateField = (layerIndex: number, ngisSourcePath: string, patch: Partial<FieldDraft>) => {
        setDirty(true);
        setLayers((prev) =>
            prev.map((layer, i) =>
                i === layerIndex
                    ? {
                          ...layer,
                          fields: layer.fields.map((f) =>
                              f.ngisSourcePath === ngisSourcePath ? { ...f, ...patch } : f,
                          ),
                      }
                    : layer,
            ),
        );
    };

    const reassign = (layerIndex: number, target: AgolLayerInfo | null) => {
        setDirty(true);
        setLayers((prev) =>
            prev.map((layer, i) => (i === layerIndex ? reassignLayer(layer, target) : layer)),
        );
    };

    const save = async () => {
        if (!binding) return;
        try {
            const saved = await updateBinding.mutateAsync({
                bindingId: binding.bindingId,
                body: toUpsertRequest(binding, {
                    mappingProfile: buildMappingProfile(binding.bindingId, layers),
                    // Holdes i takt med mappingen — API-et bruker feltet som AGOL-kolonne
                    // ved oppslag på lokalId.
                    lokalIdField: resolveLokalIdField(layers),
                }),
            });
            onSaved(saved);
        } catch {
            /* feil vises via updateBinding.error */
        }
    };

    const preflightError = errorMessage(preflight.error, 'Preflight feilet. Sjekk URL og token.');
    const saveError = errorMessage(updateBinding.error, 'Lagring feilet. Sjekk mapping og token.');
    const ready = !preflight.isPending && !preflightError && layers.length > 0;
    const selectedLayer = selectedIndex !== null ? layers[selectedIndex] : null;

    const footer = (
        <div className={styles.footer}>
            <Button variant="ghost" onClick={requestCancel} disabled={updateBinding.isPending}>
                Avbryt
            </Button>
            <div className={styles.footerEnd}>
                <Button
                    variant="primary"
                    onClick={save}
                    disabled={!ready || !mappingComplete(layers) || updateBinding.isPending}
                    leftIcon={<CheckIcon size={16} weight="bold" />}
                >
                    {updateBinding.isPending ? 'Lagrer…' : 'Lagre mapping'}
                </Button>
            </div>
        </div>
    );

    return (
        <dialog ref={dialogRef} className={styles.page} aria-label="Rediger mapping">
            <AppHeader
                title={binding ? `Rediger mapping: ${binding.displayName}` : 'Rediger mapping'}
                onBack={requestCancel}
                backLabel="Kobling"
            />

            <div className={styles.content}>
                {preflight.isPending && (
                    <div className={styles.placeholder}>
                        <Loader />
                        <p className={styles.muted}>Inspiserer AGOL-tjenesten…</p>
                    </div>
                )}

                {!preflight.isPending && preflightError && (
                    <div className={styles.singleScroll}>
                        <div className={styles.single}>
                            <Notice variant="danger">{preflightError}</Notice>
                            <div>
                                <Button variant="secondary" onClick={() => void loadDraft()}>
                                    Prøv igjen
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {ready && (
                    <>
                        <div className={styles.listPane}>
                            <LayerList
                                layers={layers}
                                selectedIndex={selectedIndex}
                                onSelect={setSelectedIndex}
                            />
                        </div>
                        <div className={styles.editorPane}>
                            {saveError && <Notice variant="danger">{saveError}</Notice>}
                            {mappingProblem(layers) && (
                                <Notice variant="warning">{mappingProblem(layers)}</Notice>
                            )}
                            {selectedLayer ? (
                                <LayerMappingPanel
                                    layer={selectedLayer}
                                    availableAgolLayers={availableAgolLayers}
                                    usedBy={layersUsedBy(layers, selectedIndex)}
                                    onLayerChange={(target) => reassign(selectedIndex!, target)}
                                    onFieldChange={(path, patch) =>
                                        updateField(selectedIndex!, path, patch)
                                    }
                                />
                            ) : (
                                <div className={styles.placeholder}>
                                    <p className={styles.muted}>
                                        Velg en objekttype i listen til venstre for å redigere
                                        mappingen.
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {footer}

            <Dialog
                open={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                title="Forkast endringene?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setConfirmCancel(false)}>
                            Fortsett
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => {
                                setConfirmCancel(false);
                                onClose();
                            }}
                        >
                            Forkast
                        </Button>
                    </>
                }
            >
                <p>Du har endret mappingen. Lukker du nå, går endringene tapt.</p>
            </Dialog>
        </dialog>
    );
};
